import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { endpoints } from "@/lib/api";
import { fmtNumber, fmtQuota } from "@/lib/format";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/Card";
import { Badge, type BadgeProps, lifecycleVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

// Dashboard — at-a-glance landing page. Pulls /api/system/stats for the
// counts, /api/system/quota for platform headroom, /api/channels?lifecycle=
// for the candidate-pool preview, and /api/evaluation-tasks?status=scored
// for the inbox count.
export default function Dashboard() {
  const stats = useQuery({
    queryKey: ["system-stats"],
    queryFn: () => endpoints.systemStats(),
    refetchInterval: 30_000,
  });
  const quota = useQuery({
    queryKey: ["system-quota"],
    queryFn: () => endpoints.systemQuota(),
    refetchInterval: 60_000,
  });
  const health = useQuery({
    queryKey: ["health"],
    queryFn: () => endpoints.health(),
  });
  const candidates = useQuery({
    queryKey: ["channels", "candidate", 5],
    queryFn: () => endpoints.channels({ lifecycle: "candidate", limit: 5 }),
  });

  const scoredCount = stats.data?.tasks_by_status?.scored ?? 0;
  const pendingCount = stats.data?.tasks_by_status?.pending ?? 0;
  const reviewCount = stats.data?.sponsorships_by_review?.pending ?? 0;

  // v2.4.7 — manual refresh. Auto-refetch runs every 30/60s but operators
  // often want to see "did my action just register" without waiting.
  const refreshAll = () => {
    void stats.refetch();
    void quota.refetch();
    void health.refetch();
    void candidates.refetch();
  };
  const refreshing =
    stats.isFetching || quota.isFetching || health.isFetching || candidates.isFetching;

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mb-4 flex items-center gap-3">
        <h1 className="text-xl font-semibold">概览</h1>
        <button
          onClick={refreshAll}
          disabled={refreshing}
          className="rounded border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50"
          title="立即重新拉取所有看板数据 (默认每 30 秒自动刷新)"
        >
          {refreshing ? "↻ 刷新中…" : "↻ 刷新"}
        </button>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <KPI
          label="待决策评估任务"
          value={scoredCount}
          to="/evaluation"
          loading={stats.isLoading}
          error={stats.isError}
          hint={
            pendingCount > 0
              ? `已评分 · 另有 ${pendingCount} 条排队中`
              : "状态 = scored"
          }
        />
        <KPI
          label="待审核推广"
          value={reviewCount}
          to="/sponsorships"
          loading={stats.isLoading}
          error={stats.isError}
          hint="状态 = pending"
        />
        <KPI
          label="活跃投放"
          value={stats.data?.campaigns_by_status?.active ?? 0}
          to="/campaigns"
          loading={stats.isLoading}
          error={stats.isError}
          hint="状态 = active"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>系统健康</CardTitle>
            <CardDescription>HTTP + DB</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {health.isLoading && <p>加载中…</p>}
            {health.isError && <p className="text-destructive">无法连接服务端</p>}
            {health.data && (
              <>
                <p>
                  <span className="text-muted-foreground">状态:</span>{" "}
                  <strong>{health.data.status}</strong>
                </p>
                <p>
                  <span className="text-muted-foreground">DB:</span>{" "}
                  {health.data.database}
                </p>
                <p>
                  <span className="text-muted-foreground">版本:</span>{" "}
                  {health.data.version}
                </p>
              </>
            )}
            <p className="pt-1 text-xs">
              <Link to="/system" className="text-primary hover:underline">
                查看完整状态 →
              </Link>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>配额头寸</CardTitle>
            <CardDescription>各平台剩余配额</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {quota.isLoading && <p>加载中…</p>}
            {quota.data?.quotas.length === 0 && (
              <p className="text-muted-foreground">尚无配额记录</p>
            )}
            {quota.data?.quotas.map((q) => (
              <div key={q.platform} className="flex items-center justify-between">
                <span className="text-muted-foreground">{q.platform}</span>
                <strong>{fmtQuota(q.remaining)}</strong>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>候选池</CardTitle>
            <CardDescription>lifecycle = candidate</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {candidates.isLoading && <p>加载中…</p>}
            {candidates.data && candidates.data.data.length === 0 && (
              <p className="text-muted-foreground">候选池为空</p>
            )}
            {candidates.data &&
              candidates.data.data.map((c) => (
                <div key={c.ID} className="flex items-center justify-between gap-2">
                  <Link
                    to={`/channels/${c.ID}`}
                    className="truncate text-primary hover:underline"
                  >
                    {c.Name ?? c.PlatformChannelID}
                  </Link>
                  <span className="text-muted-foreground">
                    {fmtNumber(c.FollowerCount)}
                  </span>
                </div>
              ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>频道生命周期</CardTitle>
            <CardDescription>
              <EntityChannelCountLine
                channelCount={totalOf(stats.data?.channels_by_lifecycle)}
              />
            </CardDescription>
          </CardHeader>
          <CardContent>
            <BucketList
              buckets={stats.data?.channels_by_lifecycle ?? {}}
              variantFor={lifecycleVariant}
            />
          </CardContent>
        </Card>

        <LLMPoolCard />
      </div>
    </div>
  );
}

// v2.8.0 Day 9 — surfaces the entity↔channel ratio on the "频道生命周期"
// card description. Entities count is pulled from /api/entities total;
// the ratio tells operators at a glance how much manual merging has
// landed (lower entity count = more channels-per-entity = more
// cross-platform KOLs identified). Falls back silently to just the
// channel count if the entities endpoint hasn't responded yet.
function EntityChannelCountLine({ channelCount }: { channelCount: number }) {
  const entitiesQ = useQuery({
    queryKey: ["entities", "list", "headcount"],
    // We only care about `total`; limit=1 keeps the payload tiny.
    queryFn: () => endpoints.listEntities({ limit: 1 }),
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
  if (entitiesQ.data === undefined) {
    return <>总数 = {channelCount}</>;
  }
  return (
    <>
      {entitiesQ.data.total} 个 entity · {channelCount} 个 channel
    </>
  );
}

// v2.2.2 — Gemini key pool health card. Polls /api/dashboard/llm-pool
// every 30s (same cadence as the sidebar badges). Hidden silently when
// LLM is disabled (endpoint returns 503 → null from the API client).
function LLMPoolCard() {
  const q = useQuery({
    queryKey: ["llm-pool-status"],
    queryFn: () => endpoints.llmPoolStatus(),
    refetchInterval: 30_000,
  });
  if (q.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>LLM Key 池</CardTitle>
          <CardDescription>加载中…</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  // LLM disabled — render an explicit "not configured" stub instead of
  // hiding entirely so the operator knows the feature exists but isn't
  // wired up. Better than silently missing a card.
  if (!q.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>LLM Key 池</CardTitle>
          <CardDescription>未配置</CardDescription>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground">
          configs/secrets.enc.yaml 里设置 llm_secrets.gemini_api_keys 后启用。
        </CardContent>
      </Card>
    );
  }
  const data = q.data;
  const sortedKeys = [...data.keys].sort((a, b) => {
    // Dead first to draw the operator's eye, then by suffix for stability.
    if (a.dead !== b.dead) return a.dead ? -1 : 1;
    return a.suffix.localeCompare(b.suffix);
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>LLM Key 池</CardTitle>
        <CardDescription>
          {data.healthy_keys}/{data.total_keys} 可用 · UTC {data.date}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1.5 text-sm">
        {sortedKeys.length === 0 && (
          <p className="text-muted-foreground">尚无 key</p>
        )}
        {sortedKeys.map((k) => {
          const models = Object.entries(k.model_usage);
          return (
            <div key={k.suffix} className="space-y-0.5">
              <div className="flex items-center gap-2">
                <code className="rounded bg-muted px-1.5 py-0.5 text-[11px]">
                  …{k.suffix}
                </code>
                {k.dead ? (
                  <Badge variant="danger" className="text-[10px]">
                    DEAD
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-[10px]">
                    healthy
                  </Badge>
                )}
              </div>
              {k.dead && k.dead_reason && (
                <p className="ml-1 text-[11px] text-destructive line-clamp-1">
                  {k.dead_reason}
                </p>
              )}
              {!k.dead && models.length > 0 && (
                <ul className="ml-1 space-y-0.5 text-[11px] text-muted-foreground">
                  {models.map(([m, used]) => {
                    const cap = data.model_caps[m] ?? 0;
                    const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
                    const warn = cap > 0 && used >= cap;
                    return (
                      <li key={m} className="flex items-center justify-between gap-2">
                        <span className="truncate">{m}</span>
                        <span
                          className={`tabular-nums ${warn ? "text-destructive font-medium" : ""}`}
                        >
                          {used}
                          {cap > 0 ? `/${cap}` : ""}
                          {cap > 0 && ` (${pct}%)`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              {!k.dead && models.length === 0 && (
                <p className="ml-1 text-[11px] text-muted-foreground">未使用</p>
              )}
            </div>
          );
        })}
        <KeyTestSection />
      </CardContent>
    </Card>
  );
}

// v2.2.5 — KeyTestSection lets the operator paste a candidate Gemini
// API key and run one cheap probe (through CF gateway in prod) to
// confirm it works BEFORE going through the sops-encrypt + scp +
// restart rotation. On success the panel shows the 4-line rotation
// guide so the operator can copy/paste and finish on prod.
function KeyTestSection() {
  const [open, setOpen] = useState(false);
  const [candidate, setCandidate] = useState("");
  const mut = useMutation({
    mutationFn: (key: string) => endpoints.llmPoolKeyTest(key),
  });

  if (!open) {
    return (
      <div className="pt-2 mt-2 border-t border-border">
        <button
          onClick={() => setOpen(true)}
          className="text-[11px] text-primary hover:underline"
        >
          + 测试新 key(验证再上线)
        </button>
      </div>
    );
  }

  const close = () => {
    setOpen(false);
    setCandidate("");
    mut.reset();
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!candidate.trim()) return;
    mut.mutate(candidate.trim());
  };

  return (
    <div className="pt-2 mt-2 border-t border-border space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium">测试新 Gemini key</span>
        <button
          onClick={close}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          关闭
        </button>
      </div>
      <form onSubmit={submit} className="space-y-1.5">
        <Input
          type="password"
          value={candidate}
          onChange={(e) => setCandidate(e.target.value)}
          placeholder="AIzaSy..."
          className="text-[11px] font-mono"
          disabled={mut.isPending || mut.isSuccess}
        />
        <p className="text-[10px] text-muted-foreground">
          后端只调一次 ~10 token 的探测,不写入 secrets。
        </p>
        {!mut.isSuccess && (
          <Button
            type="submit"
            size="sm"
            className="w-full"
            disabled={mut.isPending || !candidate.trim()}
          >
            {mut.isPending ? "测试中…" : "测试"}
          </Button>
        )}
      </form>
      {mut.isError && (
        <div className="rounded border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-[11px]">
          <div className="font-medium text-destructive">✗ 测试失败</div>
          <div className="mt-0.5 text-muted-foreground break-all">
            {(mut.error as Error).message}
          </div>
        </div>
      )}
      {mut.isSuccess && mut.data && (
        <RotationGuide
          suffix={mut.data.echoed_suffix}
          model={mut.data.model}
          tokensIn={mut.data.tokens_in}
          tokensOut={mut.data.tokens_out}
          onClose={close}
        />
      )}
    </div>
  );
}

function RotationGuide({
  suffix,
  model,
  tokensIn,
  tokensOut,
  onClose,
}: {
  suffix: string;
  model: string;
  tokensIn: number;
  tokensOut: number;
  onClose: () => void;
}) {
  return (
    <div className="space-y-2 rounded border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-[11px]">
      <div className="font-medium text-emerald-900">
        ✓ Key 验证通过 (…{suffix})
      </div>
      <div className="text-emerald-800/80">
        {model} · {tokensIn} in / {tokensOut} out tokens
      </div>
      <div className="mt-1 text-emerald-900">在 prod 执行以下命令永久启用:</div>
      <pre className="overflow-x-auto rounded bg-emerald-900/90 p-2 text-[10px] text-emerald-50 leading-snug">
{`SOPS_AGE_KEY_FILE=/opt/bitbase-kol/secrets/age.key \\
  sops --decrypt /opt/bitbase-kol/configs/secrets.enc.yaml \\
  | yq '.llm_secrets.gemini_api_keys += ["AIza...你的新key..."]' \\
  | sops --encrypt --age age1z4sl... --input-type yaml --output-type yaml /dev/stdin \\
  > /opt/bitbase-kol/configs/secrets.enc.yaml.new && \\
mv /opt/bitbase-kol/configs/secrets.enc.yaml.new /opt/bitbase-kol/configs/secrets.enc.yaml && \\
systemctl restart bitbase-kol`}
      </pre>
      <div className="text-emerald-900/80">
        或者更简单:本地 <code className="font-mono">sops configs/secrets.enc.yaml</code>{" "}
        编辑、scp、重启。
      </div>
      <Button size="sm" variant="outline" onClick={onClose} className="mt-1">
        关闭
      </Button>
    </div>
  );
}

function KPI({
  label,
  value,
  to,
  hint,
  loading,
  error,
}: {
  label: string;
  value: number;
  to: string;
  hint?: string;
  loading?: boolean;
  error?: boolean;
}) {
  // v2.13.x — a KPI of `count ?? 0` rendered an in-flight request, a 500, and
  // a genuine zero identically as "0". The operator reads "待决策 = 0" as
  // "nothing to do" even when the backend just fell over. Distinguish them:
  // "…" while loading, a red "!" on error, the number only when known.
  return (
    <Link
      to={to}
      className="block rounded-lg border border-border bg-background p-4 hover:bg-muted/50"
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      {error ? (
        <div className="mt-1 text-3xl font-semibold text-destructive" title="加载失败">!</div>
      ) : loading ? (
        <div className="mt-1 text-3xl font-semibold text-muted-foreground">…</div>
      ) : (
        <div className="mt-1 text-3xl font-semibold">{value}</div>
      )}
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </Link>
  );
}

function BucketList({
  buckets,
  variantFor,
}: {
  buckets: Record<string, number>;
  variantFor: (s: string) => BadgeProps["variant"];
}) {
  const entries = Object.entries(buckets).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) {
    return <p className="text-xs text-muted-foreground">暂无</p>;
  }
  return (
    <ul className="space-y-1 text-sm">
      {entries.map(([bucket, n]) => (
        <li key={bucket} className="flex items-center justify-between">
          <Badge variant={variantFor(bucket) ?? "secondary"}>{bucket}</Badge>
          <span className="font-medium">{n}</span>
        </li>
      ))}
    </ul>
  );
}

function totalOf(buckets: Record<string, number> | undefined): number {
  if (!buckets) return 0;
  return Object.values(buckets).reduce((s, n) => s + n, 0);
}
